import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { RadioGroup, Input, TextArea } from '@progress/kendo-react-inputs';
import VideoPreview, { youtube_parser } from '../../VideoPreview';
import Uploader, { uploaderTypes } from '../../Uploader';
import ListEditor from '../../ListEditor';
import { LocalizationContext } from '../../../contexts';
import PublishSwitch from '../publishSwitch';
import './index.scss';
import '../forms.scss';

export default function MarketingBanner({ data, settingsData = {}, onUpdate, validator, ...rest }) {
  const bannerTypes = settingsData.bannerTypes || ["hero", "triple"];
  const emptyItem = useRef({
    name: "",
    type: bannerTypes[0],
    hero: { title: "", type: "image", image: "", image_mobile: "", animate_json: "", animate_json_mobile: "", link: "", link_target: "_top" },
    triple: {
      title: "", items: [
        { title: "", text: "", type: "image", image: "", image_mobile: "", animate_json: "", animate_json_mobile: "", link: "", link_target: "_top" },
        { title: "", text: "", type: "image", image: "", image_mobile: "", animate_json: "", animate_json_mobile: "", link: "", link_target: "_top" },
        { title: "", text: "", type: "image", image: "", image_mobile: "", animate_json: "", animate_json_mobile: "", link: "", link_target: "_top" }
      ]
    },
    video: { title: "", video: "", video_poster: "", video_poster_mobile: "" }
  });
  const generalEmptyItem = useRef(JSON.parse(JSON.stringify(emptyItem.current)));
  const personalEmptyItem = useRef({ ...JSON.parse(JSON.stringify(emptyItem.current)) });

  const [compData, setCompData] = useState({});
  const [localization] = useContext(LocalizationContext);
  const { labels } = localization;

  validator.current = () => {
    return true;
  };

  useEffect(() => {
    try {
      let defaultData = { items: [settingsData.type === "general" ? JSON.parse(JSON.stringify(generalEmptyItem.current)) : JSON.parse(JSON.stringify(personalEmptyItem.current))] };
      const startCompData = { ...defaultData, ...JSON.parse(JSON.stringify(data ? data : {})) };
      if (startCompData.items.length === 0) {
        startCompData.items = [settingsData.type === "general" ? JSON.parse(JSON.stringify(generalEmptyItem.current)) : JSON.parse(JSON.stringify(personalEmptyItem.current))];
      }
      startCompData.items.forEach(banner => {
        if (banner && banner.triple.items.length < 0) {
          for (let i = banner.triple.items.length; i < 3; i++) {
            banner.triple.items.push({ title: "", text: "", type: "image", image: "", image_mobile: "", animate_json: "", animate_json_mobile: "", link: "", link_target: "_top" });
          }
        }
      });
      setCompData(startCompData);
    }
    catch (e) {

    }
  }, [data]);

  const canPublish = (item) => {
    if (!item.name) {
      return false;
    }

    if (!item.type || item.type === "hero") {
      if (!item.hero.title) {
        return false;
      }
      if ((!item.hero.type || item.hero.type === "image") && !(item.hero.image && item.hero.image_mobile)) {
        return false;
      }
      if (item.hero.type === "animate" && !(item.hero.animate_json && item.hero.animate_json_mobile)) {
        return false;
      }
    }

    if (item.type === "triple") {
      if (!item.triple.title) {
        return false;
      }
      if (!item.triple.items || !item.triple.items.length || item.triple.items.length !== 3) {
        return false;
      }
      for (let i = 0; i < item.triple.items.length; i++) {
        if (!canPublishTripleItem(item.triple.items[i])) {
          i = item.triple.items.length;
          return false;
        }
      }
    }
    
    if (item.type === "video") {
      if (!(item.video.title && item.video.video && item.video.video_poster && item.video.video_poster_mobile)) {
        return false;
      }
    }
    return true;
  };

  const canPublishTripleItem = (tripleItem) => {
    if (!tripleItem.title) {
      return false;
    }
    if (!tripleItem.text) {
      return false;
    }
    if ((!tripleItem.type || tripleItem.type === "image") && !(tripleItem.image && tripleItem.image_mobile)) {
      return false;
    }
    if (tripleItem.type === "animate" && !(tripleItem.animate_json && tripleItem.animate_json_mobile)) {
      return false;
    }
    return true;
  };

  const isEmptyItem = () => {
    return JSON.stringify(compData) === JSON.stringify(emptyItem.current)
  }

  const showRequired = (requiredField) => {
    return (!isEmptyItem() && !requiredField)
  }

  const updateItem = useCallback((i, newData, key) => {
    let newCompData;
    if (i !== null) {
      newCompData = { ...compData };
      if (key) {
        newCompData.items[i][key] = newData;
      }
      else {
        newCompData.items[i] = { ...newCompData.items[i], ...newData };
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
    } else {
      newCompData = { ...compData, ...newData };
    }
    setCompData(newCompData);
    onUpdate(newCompData);
  }, [compData]);

  const updateItemHeroBanner = useCallback((i, newData, key) => {
    if (i !== null) {
      let newCompData;
      newCompData = { ...compData };
      if (key) {
        newCompData.items[i].hero[key] = newData;
      }
      else {
        newCompData.items[i].hero = { ...newCompData.items[i].hero, ...newData };
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
      setCompData(newCompData);
      onUpdate(newCompData);
    }
  }, [compData]);

  const updateItemTripleBanner = useCallback((i, tripleIndex, newData, key) => {
    if (i !== null) {
      let newCompData;
      newCompData = { ...compData };
      if (tripleIndex !== null) {
        if (key) {
          newCompData.items[i].triple.items[tripleIndex][key] = newData;
        }
        else {
          newCompData.items[i].triple.items[tripleIndex] = { ...newCompData.items[i].triple.items[tripleIndex], ...newData };
        }
      }
      else {
        if (key) {
          newCompData.items[i].triple[key] = newData;
        }
        else {
          newCompData.items[i].triple = { ...newCompData.items[i].triple, ...newData };
        }
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
      setCompData(newCompData);
      onUpdate(newCompData);
    }
  }, [compData]);

  const updateItemVideoBanner = useCallback((i, newData, key) => {
    if (i !== null) {
      let newCompData;
      newCompData = { ...compData };
      if (key) {
        newCompData.items[i].video[key] = newData;
      }
      else {
        newCompData.items[i].video = { ...newCompData.items[i].video, ...newData };
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
      setCompData(newCompData);
      onUpdate(newCompData);
    }
  }, [compData]);

  const title = (settings) => {
    return settingsData.title ? eval("`" + settingsData.title + "`") : `[${settings.listLength}/${settings.maxSize}]`;
  };

  const itemTitle = (item, settings) => {
    return `${labels.pagesManagement.marketingBanner.banner} ${settings.index + 1}: ${item.name} (${labels.pagesManagement.marketingBanner["banner_type_" + (item.type || "hero")]}) (${!canPublish(item) ? labels.pagesManagement.forms.publish_not_ready : item.isPublish ? labels.pagesManagement.forms.is_publish : labels.pagesManagement.forms.publish_ready})`;
  };

  const itemContent = (item, settings) => {
    return <>
      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.marketingBanner.add_name}</label>
        <Input name={'name'} value={item.name} onChange={({ value }) => updateItem(settings.index, value, 'name')}></Input>
        {showRequired(item.name) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>
      <div className="form-field">
        <label className="form-field__label inline">{labels.pagesManagement.marketingBanner.banner_type}</label>
        <RadioGroup
          data={bannerTypes.map(type => { return { label: labels.pagesManagement.marketingBanner[`banner_type_${type}`], value: type } })}
          layout='horizontal'
          value={item.type && bannerTypes.indexOf(item.type) >= 0 ? item.type : bannerTypes[0]}
          onChange={({ value }) => {
            updateItem(settings.index, { type: value });
          }}
        />
      </div>

      {(item.type === "video" || (!item.type && bannerTypes[0] === "video")) && <>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.videoBanner.add_video_title}</label>
          <Input name={'title'} value={item.video.title} onChange={({ value }) => updateItemVideoBanner(settings.index, value, 'title')}></Input>
          {showRequired(item.video.title) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_youtube_link}</label>
          <Input name={'video_url'} value={item.video.video} style={{ direction: 'ltr', textAlign: 'left' }} onChange={({ value }) => updateItemVideoBanner(settings.index, value, 'video')}></Input>
          <VideoPreview videoUrl={item.video.video} />
          {showRequired(item.video.video) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.videoBanner.add_video_poster}</label>
          <Uploader type={uploaderTypes.image} imageUrl={item.video.video_poster} prefix={'videoBanner'} onChange={(file) => { updateItemVideoBanner(settings.index, { video_poster: file }) }} />
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.videoBanner.add_video_poster_mobile}</label>
          <Uploader type={uploaderTypes.image} imageUrl={item.video.video_poster_mobile} prefix={'videoBanner'} onChange={(file) => { updateItemVideoBanner(settings.index, { video_poster_mobile: file }) }} />
        </div>
      </>}

      {(item.type === "hero" || (!item.type && bannerTypes[0] === "hero")) && <>
        <div className="form-field">
          <label className="form-field__label inline">{labels.pagesManagement.marketingBanner.display_type}</label>
          <RadioGroup data={[
            { label: labels.pagesManagement.marketingBanner.display_type_image, value: 'image' },
            { label: labels.pagesManagement.marketingBanner.display_type_animate, value: 'animate' }
          ]}
            layout='horizontal'
            value={item.hero.type || "image"}
            onChange={({ value }) => {
              updateItemHeroBanner(settings.index, { type: value });
            }}
          />
        </div>

        {(!item.hero.type || item.hero.type === "image") && <>
          <div className="form-field">
            <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_pc_banner}</label>
            <Uploader type={uploaderTypes.image} imageUrl={item.hero.image} prefix={'heroBanner'} onChange={(file) => { updateItemHeroBanner(settings.index, { image: file }) }} />
          </div>
          <div className="form-field">
            <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_mobile_banner}</label>
            <Uploader type={uploaderTypes.image} imageUrl={item.hero.image_mobile} prefix={'heroBanner'} onChange={(file) => { updateItemHeroBanner(settings.index, { image_mobile: file }) }} />
          </div>
        </>}

        {item.hero.type === "animate" && <>
          <div className="form-field">
            <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_pc_animation}</label>
            <Uploader type={uploaderTypes.animation} animationUrl={item.hero.animate_json} prefix={'heroBanner'} onChange={(file) => { updateItemHeroBanner(settings.index, { animate_json: file }) }} />
          </div>
          <div className="form-field">
            <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_mobile_animation}</label>
            <Uploader type={uploaderTypes.animation} animationUrl={item.hero.animate_json_mobile} prefix={'heroBanner'} onChange={(file) => { updateItemHeroBanner(settings.index, { animate_json_mobile: file }) }} />
          </div>
        </>}

        <div className="form-field">
          <label className="form-field__label">{labels.pagesManagement.forms.add_link}</label>
          <div className="form-field--link">
            <Input name={'link'} value={item.hero.link} onChange={({ value }) => updateItemHeroBanner(settings.index, value, 'link')}></Input>
            <RadioGroup data={[
              { label: labels.pagesManagement.forms.open_top, value: '_top' },
              { label: `${labels.pagesManagement.forms.open_blank} (${labels.pagesManagement.forms.for_external_links})`, value: '_blank' }
            ]}
              layout='horizontal'
              value={item.hero.link_target}
              onChange={({ value }) => {
                updateItemHeroBanner(settings.index, { link_target: value });
              }}
            />
          </div>
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_accessibility_title} ({labels.pagesManagement.forms.banner_short_description})</label>
          <Input name={'title'} value={item.hero.title} onChange={({ value }) => updateItemHeroBanner(settings.index, value, 'title')}></Input>
        </div>
      </>}

      {(item.type === "triple" || (!item.type && bannerTypes[0] === "triple")) && <>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_triple_title}</label>
          <Input name={'title'} value={item.triple.title} onChange={({ value }) => updateItemTripleBanner(settings.index, null, value, 'title')}></Input>
          {showRequired(item.triple.title) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
        </div>

        <div className="form-item">
          <div className="form-item__title">
            {labels.pagesManagement.forms.must_add_3_triple_items}
          </div>
        </div>

        <ListEditor
          isSortable={true}
          canRemove={true}
          canAdd={true}
          removeContentOnClose={false}
          maxSize={3}
          minSize={3}
          addNewItemBtnText={labels.pagesManagement.marketingBanner.addNewTripleItemBtnText}
          title={false}
          emptyItem={{ ...emptyItem.current.triple.items[0] }}
          itemTitle={(tripleItem, tripleItemSettings) => {
            return `${labels.pagesManagement.marketingBanner.triple_item_title} ${tripleItemSettings.index + 1}: ${tripleItem.title}${!canPublishTripleItem(tripleItem) ? ' (' + labels.pagesManagement.marketingBanner.triple_item_missing_content + ')' : ''}`;
          }}
          itemContent={(tripleItem, tripleItemSettings) => {
            return <>
              <div className="form-field">
                <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_title}</label>
                <Input name={'title'} value={tripleItem.title} onChange={({ value }) => updateItemTripleBanner(settings.index, tripleItemSettings.index, value, 'title')}></Input>
                {showRequired(tripleItem.title) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
              </div>
              <div className="form-field">
                <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_text}</label>
                <TextArea name={'text'} value={tripleItem.text} onChange={({ value }) => updateItemTripleBanner(settings.index, tripleItemSettings.index, value, 'text')}></TextArea>
                {showRequired(tripleItem.text) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
              </div>

              <div className="form-field">
                <label className="form-field__label inline">{labels.pagesManagement.marketingBanner.display_type}</label>
                <RadioGroup data={[
                  { label: labels.pagesManagement.marketingBanner.display_type_image, value: 'image' },
                  { label: labels.pagesManagement.marketingBanner.display_type_animate, value: 'animate' }
                ]}
                  layout='horizontal'
                  value={tripleItem.type || "image"}
                  onChange={({ value }) => {
                    updateItemTripleBanner(settings.index, tripleItemSettings.index, { type: value });
                  }}
                />
              </div>

              {(!tripleItem.type || tripleItem.type === "image") && <>
                <div className="form-field">
                  <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_pc_banner}</label>
                  <Uploader type={uploaderTypes.image} imageUrl={tripleItem.image} prefix={'triple'} onChange={(file) => { updateItemTripleBanner(settings.index, tripleItemSettings.index, { image: file }) }} />
                </div>
                <div className="form-field">
                  <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_mobile_banner}</label>
                  <Uploader type={uploaderTypes.image} imageUrl={tripleItem.image_mobile} prefix={'triple'} onChange={(file) => { updateItemTripleBanner(settings.index, tripleItemSettings.index, { image_mobile: file }) }} />
                </div>
              </>}

              {tripleItem.type === "animate" && <>
                <div className="form-field">
                  <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_pc_animation}</label>
                  <Uploader type={uploaderTypes.animation} animationUrl={tripleItem.animate_json} prefix={'triple'} onChange={(file) => { updateItemTripleBanner(settings.index, tripleItemSettings.index, { animate_json: file }) }} />
                </div>
                <div className="form-field">
                  <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_mobile_animation}</label>
                  <Uploader type={uploaderTypes.animation} animationUrl={tripleItem.animate_json_mobile} prefix={'triple'} onChange={(file) => { updateItemTripleBanner(settings.index, tripleItemSettings.index, { animate_json_mobile: file }) }} />
                </div>
              </>}

              <div className="form-field">
                <label className="form-field__label">{labels.pagesManagement.forms.add_link}</label>
                <div className="form-field--link">
                  <Input name={'link'} value={tripleItem.link} onChange={({ value }) => updateItemTripleBanner(settings.index, tripleItemSettings.index, value, 'link')}></Input>
                  <RadioGroup data={[
                    { label: labels.pagesManagement.forms.open_top, value: '_top' },
                    { label: `${labels.pagesManagement.forms.open_blank} (${labels.pagesManagement.forms.for_external_links})`, value: '_blank' }
                  ]}
                    layout='horizontal'
                    value={tripleItem.link_target}
                    onChange={({ value }) => {
                      updateItemTripleBanner(settings.index, tripleItemSettings.index, { link_target: value });
                    }}
                  />
                </div>
              </div>
            </>;
          }}
          list={[...(item.triple.items || [])]}
          onListUpdate={(newList) => { updateItemTripleBanner(settings.index, null, { items: newList }) }}
        />
      </>}
      <PublishSwitch isPublish={item?.isPublish} canPublish={canPublish(item)}
        updateItem={(event) => updateItem(settings.index, { isPublish: event.target.value })} />
    </>;
  };

  return <ListEditor
    isSortable={true}
    canRemove={true}
    canAdd={true}
    removeContentOnClose={false}
    maxSize={settingsData.maxSize || 3}
    minSize={settingsData.minSize || 0}
    className="marketingBanner-listEditor"
    addNewItemBtnText={labels.pagesManagement.marketingBanner.addNewItemBtnText}
    title={title}
    emptyItem={settingsData.type === "general" ? generalEmptyItem.current : personalEmptyItem.current}
    itemTitle={itemTitle}
    itemContent={itemContent}
    list={[...(compData.items || [])]}
    onListUpdate={(newList) => { updateItem(null, { items: newList }) }}
  />;
};